import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import authService from '../services/authService';
import Header from '../components/Header';
import './css/VerifyEmail.css';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError('No verification token provided. Please check your email link.');
        setVerifying(false);
        return;
      }

      try {
        setVerifying(true);
        const result = await authService.verifyEmail(token);
        
        if (result.success && result.data) {
          setSuccess(true);
          setEmail(result.data.email || '');
        } else {
          setError(result.error || 'Failed to verify email. The link may be invalid or expired.');
        }
      } catch (err) {
        console.error('Email verification error:', err);
        setError('Failed to verify email. The link may be invalid or expired.');
      } finally {
        setVerifying(false);
      }
    };

    verifyEmail();
  }, [token]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    if (!token) {
      setResendMessage('Unable to resend: verification token not available');
      return;
    }

    if (resendCooldown > 0) {
      setResendMessage(`Please wait ${resendCooldown} seconds before resending.`);
      return;
    }

    try {
      setResending(true);
      setResendMessage('');
      
      const result = await authService.resendVerificationEmail(token);

      if (result.success) {
        // Start 60-second cooldown
        setResendCooldown(60);
        // Clear the original error message
        setError('');
        
        if (result.data?.email_sent) {
          setResendMessage('We have sent a new link to verify your email. Please check your email for the updated link.');
        } else if (result.data?.token_already_replaced) {
          setResendMessage('We had already sent a new link to verify your email. Please check your email for the updated link.');
        } else if (result.data?.already_verified) {
          setResendMessage('Email is already verified. You can log in.');
        } else {
          setResendMessage(result.data?.message || 'Verification email sent. Please check your inbox.');
        }
      } else {
        setResendMessage(result.error || 'Failed to resend verification email. Please try again later.');
      }
    } catch (err) {
      console.error('Resend verification error:', err);
      setResendMessage('Failed to resend verification email. Please try again later.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="verify-email-page">
      <Header />
      
      <div className="verify-email-container">
        <div className="verify-email-card">
          {verifying && (
            <div className="verify-status verifying">
              <div className="spinner"></div>
              <h1>Verifying Your Email</h1>
              <p>Please wait while we verify your email address...</p>
            </div>
          )}

          {!verifying && success && (
            <div className="verify-status success">
              <div className="success-icon">✓</div>
              <h1>Email Verified Successfully!</h1>
              {email && (
                <p className="verified-email">Your email <strong>{email}</strong> has been verified.</p>
              )}
              <p className="success-description">Your account is now active. You can proceed to login and start using the application.</p>
              <Link to="/login" className="btn btn-primary login-btn">
                Go to Login
              </Link>
            </div>
          )}

          {!verifying && (error || resendMessage) && !success && (
            <div className="verify-status error">
              {resending ? (
                <>
                  <div className="spinner"></div>
                  <h1>Resending Verification Email</h1>
                  <p>Please wait while we send you a new verification link...</p>
                </>
              ) : (
                <>
                  <div className="error-icon">✗</div>
                  <h1>Verification Failed</h1>
                  {error && <p className="error-message">{error}</p>}
                  {resendMessage && (
                    <p className={`resend-message ${resendMessage.includes('sent') || resendMessage.includes('already verified') || resendMessage.includes('already sent') ? 'success' : 'error'}`}>
                      {resendMessage}
                    </p>
                  )}
                  <div className="error-actions">
                    <button 
                      onClick={handleResendVerification}
                      disabled={resendCooldown > 0}
                      className="btn btn-secondary resend-btn"
                    >
                      {resendCooldown > 0 
                        ? `Resend in ${resendCooldown}s` 
                        : 'Resend Verification Email'
                      }
                    </button>
                    <Link to="/login" className="btn btn-primary">
                      Try to Login
                    </Link>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;